import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { CheckCircle2, MapPin, Search, Heart } from "lucide-react";
import { PageHead, Panel } from "@/project/components/dashboard-shell";
import { formatINR, initiatives } from "@/project/data/sample";
import { campaigns, partners, partnerTypes, domainName, getPartner } from "@/project/data/network";

export const Route = createFileRoute("/donor/discover")({ component: Discover });

function Discover() {
  const [q, setQ] = useState("");
  const [domain, setDomain] = useState("all");
  const [type, setType] = useState("all");

  const orgs = useMemo(
    () =>
      partners.filter(
        (p) =>
          (domain === "all" || p.domain === domain) &&
          (type === "all" || p.type === type) &&
          (q === "" || `${p.name} ${p.city} ${p.state}`.toLowerCase().includes(q.toLowerCase())),
      ),
    [q, domain, type],
  );

  const live = useMemo(
    () =>
      campaigns.filter(
        (c) =>
          c.status === "Live" &&
          (domain === "all" || c.domain === domain) &&
          (q === "" || c.title.toLowerCase().includes(q.toLowerCase())),
      ),
    [q, domain],
  );

  return (
    <>
      <PageHead title="Discover" subtitle="Search verified partner organisations and live campaigns to support." />

      <div className="grid gap-3 rounded-2xl border border-border/60 bg-card p-4 sm:grid-cols-3">
        <label className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
          <Search className="h-4 w-4 text-foreground/70" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search organisation, city or campaign" className="w-full bg-transparent text-sm outline-none" />
        </label>
        <select value={domain} onChange={(e) => setDomain(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
          <option value="all">All giving domains</option>
          {initiatives.map((i) => <option key={i.slug} value={i.slug}>{i.name}</option>)}
        </select>
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-xl border border-border bg-background px-3 py-2 text-sm">
          <option value="all">All organisation types</option>
          {partnerTypes.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <h2 className="mt-8 mb-3 font-display text-lg font-semibold text-[var(--maroon-deep)]">{orgs.length} organisations</h2>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {orgs.slice(0, 12).map((p) => (
          <div key={p.id} className="flex flex-col rounded-2xl border border-border/60 bg-card p-5">
            <p className="text-xs uppercase tracking-wider text-foreground/70">{p.type} · {domainName(p.domain)}</p>
            <h3 className="mt-1 font-display text-base font-semibold leading-snug">{p.name}</h3>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-foreground/60"><MapPin className="h-3.5 w-3.5" /> {p.city}, {p.state}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
              {p.verified && <span className="inline-flex items-center gap-1 rounded-full bg-[oklch(0.93_0.06_155)] px-2 py-0.5 text-[var(--success)]"><CheckCircle2 className="h-3 w-3" /> Verified</span>}
              {p.has80G && <span className="rounded-full bg-[var(--cream-2)] px-2 py-0.5">80G</span>}
            </div>
            <div className="mt-4 flex gap-2">
              <Link to="/organisations/$id" params={{ id: p.id }} className="rounded-full border border-border px-3 py-1.5 text-xs font-semibold">View</Link>
              <Link to="/donate" search={{ org: p.id }} className="inline-flex items-center gap-1 rounded-full bg-[var(--maroon)] px-3 py-1.5 text-xs font-semibold text-[var(--cream)]">
                <Heart className="h-3 w-3" /> Donate
              </Link>
            </div>
          </div>
        ))}
      </div>

      <h2 className="mt-10 mb-3 font-display text-lg font-semibold text-[var(--maroon-deep)]">{live.length} live campaigns</h2>
      <Panel title="Campaigns matching your search">
        <ul className="divide-y divide-border text-sm">
          {live.slice(0, 10).map((c) => {
            const org = getPartner(c.orgId);
            const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
            return (
              <li key={c.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-[220px] flex-1">
                  <Link to="/campaigns/$id" params={{ id: c.id }} className="font-medium hover:underline">{c.title}</Link>
                  <p className="text-xs text-foreground/70">{org?.name} · {domainName(c.domain)} · {pct}% funded</p>
                </div>
                <span className="text-xs font-semibold text-[var(--maroon)]">{formatINR(c.raised)}</span>
                <Link to="/donate" search={{ campaign: c.id, org: c.orgId }} className="rounded-full bg-[var(--maroon)] px-3 py-1.5 text-xs font-semibold text-[var(--cream)]">Donate</Link>
              </li>
            );
          })}
        </ul>
      </Panel>
    </>
  );
}